import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { supabase } from '../../supabaseClient';
import ImagingLibraryBrowser from '../ImageLibraryPicker';

const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_BORDER = '#60A5FA';

function buildPublicUrl(bucket, filePath) {
  const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
  return data.publicUrl;
}

export default function PatientRadiology({ data = [], onChange, patientId }) {
  const handleAddSelectedFromBrowser = (selectedItem) => {
    const exists = data.some(
      (img) => img.library_key === selectedItem.library_key
    );

    if (exists) return;

    const next = [
      ...data,
      {
        image_id: selectedItem.id,
        image_source: selectedItem.source,
        library_key: selectedItem.library_key,

        title: selectedItem.title,
        bucket: selectedItem.bucket,
        file_path: selectedItem.file_path,
        imageUrl: selectedItem.imageUrl,

        modality: selectedItem.modality,
        study_type: selectedItem.study_type || selectedItem.image_type || '',
        body_part: selectedItem.body_part || '',
        preview_label: selectedItem.preview_label || '',
        description: selectedItem.description || '',

        report_visible: false,
        report: selectedItem.report || '',

        time_offset: 0,
        time_offset_unit: 'hours',
      },
    ];

    onChange(next);
  };

  const updatePatientImage = (index, patch) => {
    const next = data.map((img, i) => (i === index ? { ...img, ...patch } : img));
    onChange(next);
  };

  const removePatientImage = (index) => {
    const next = data.filter((_, i) => i !== index);
    onChange(next);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <ImagingLibraryBrowser
        title="Patient Radiology"
        subtitle="Search the image library and add selected imaging to this patient record."
        actionLabel="Add to patient"
        actionDisabledLabel="Already added"
        defaultIncludePublic={false}
        defaultIncludeUser
        showSourceToggles
        onAction={handleAddSelectedFromBrowser}
        isActionDisabled={(item) =>
          data.some((img) => img.library_key === item.library_key)
        }
      />

      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${ACCENT_BORDER}`,
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'white',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
          }}
        >
          <Typography sx={{ fontWeight: 700 }}>Added to patient record</Typography>
        </Box>

        <Box sx={{ p: 2 }}>
          {data.length === 0 ? (
            <Typography color="text.secondary">
              No imaging has been added yet.
            </Typography>
          ) : (
            <Stack spacing={2}>
              {data.map((img, index) => (
                <Paper
                  key={`${img.library_key || img.image_id}-${index}`}
                  elevation={0}
                  sx={{
                    border: '1px solid #d1d5db',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', lg: 'flex-start' }}
                  >
                    <Box
                      sx={{
                        width: { xs: '100%', lg: 220 },
                        minWidth: { lg: 220 },
                      }}
                    >
                      <Box
                        component="img"
                        src={img.imageUrl || buildPublicUrl(img.bucket, img.file_path)}
                        alt={img.title}
                        sx={{
                          width: '100%',
                          height: 160,
                          objectFit: 'cover',
                          borderRadius: 2,
                          border: '1px solid #e5e7eb',
                          bgcolor: '#f8fafc',
                        }}
                      />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                      <Stack
                        direction={{ xs: 'column', md: 'row' }}
                        justifyContent="space-between"
                        spacing={1.5}
                        sx={{ mb: 1.5 }}
                      >
                        <Box>
                          <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>
                            {img.title}
                          </Typography>

                          <Stack
                            direction="row"
                            spacing={0.75}
                            useFlexGap
                            flexWrap="wrap"
                            sx={{ mt: 0.75 }}
                          >
                            {img.modality && <Chip size="small" label={img.modality} />}

                            {img.study_type && (
                              <Chip
                                size="small"
                                label={img.study_type}
                                variant="outlined"
                              />
                            )}

                            {img.body_part && (
                              <Chip
                                size="small"
                                label={img.body_part}
                                variant="outlined"
                              />
                            )}

                            {img.image_source && (
                              <Chip
                                size="small"
                                label={img.image_source === 'user' ? 'My upload' : 'Public'}
                                color={img.image_source === 'user' ? 'primary' : 'default'}
                                variant={img.image_source === 'user' ? 'filled' : 'outlined'}
                              />
                            )}
                          </Stack>
                        </Box>

                        <Button
                          color="error"
                          variant="text"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => removePatientImage(index)}
                          sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                        >
                          Remove
                        </Button>
                      </Stack>

                      <Stack spacing={2}>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={!!img.report_visible}
                              onChange={(e) =>
                                updatePatientImage(index, {
                                  report_visible: e.target.checked,
                                })
                              }
                            />
                          }
                          label="Report visible to participant"
                        />

                        <TextField
                          label="Radiology report"
                          multiline
                          minRows={3}
                          value={img.report ?? ''}
                          onChange={(e) =>
                            updatePatientImage(index, { report: e.target.value })
                          }
                          fullWidth
                        />

                        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                          <TextField
                            label="Time offset"
                            type="number"
                            value={img.time_offset ?? 0}
                            onChange={(e) =>
                              updatePatientImage(index, {
                                time_offset:
                                  e.target.value === '' ? '' : Number(e.target.value),
                              })
                            }
                            sx={{ width: 180 }}
                          />

                          <TextField
                            select
                            label="Offset unit"
                            value={img.time_offset_unit ?? 'hours'}
                            onChange={(e) =>
                              updatePatientImage(index, {
                                time_offset_unit: e.target.value,
                              })
                            }
                            sx={{ width: 180 }}
                          >
                            <MenuItem value="minutes">Minutes</MenuItem>
                            <MenuItem value="hours">Hours</MenuItem>
                            <MenuItem value="days">Days</MenuItem>
                            <MenuItem value="months">Months</MenuItem>
                          </TextField>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
        </Box>
      </Paper>
    </Box>
  );
}